import { useLayoutEffect, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import type { KeyboardEventHandler, RefObject } from 'react'
import clsx from 'clsx'
import PopoverScrollArea from './PopoverScrollArea'
import {
  MENU_OPTION_LIST_CLASS,
  VIEW_PARAMETER_DROPDOWN_FRAME_CLASS,
  VIEW_PARAMETER_DROPDOWN_SCROLLBAR_GUTTER_CLASS,
  VIEW_PARAMETER_DROPDOWN_TRAILING_SCROLLBAR_GUTTER_CLASS,
} from './compactDropdownStyles'
import { IconButton } from './IconButton'
import { ChevronDownIcon } from '../icons/icons'
import { ValueSelectorSubmenu } from './ValueSelectorSubmenu'
import InfoHint from './InfoHint'
import { Tooltip } from './Tooltip'
import { optionToneTextClass } from './pageHeaderStatusTones'
import {
  ValueSelectorOptionButton,
  ValueSelectorSearchInput,
  ValueSelectorSecondaryLabel,
} from './ValueSelectorParts'
import type { ValueSelectorOption } from './ValueSelector'
import type { ValueSelectorVariant } from './ValueSelectorTrigger'

export interface ValueSelectorDropdownPosition {
  top: number
  left: number
  width: number
  maxHeight: number
  placedAbove: boolean
  triggerWidth: number
}

interface ValueSelectorDropdownProps {
  dropdownRef: RefObject<HTMLDivElement>
  optionListRef: RefObject<HTMLDivElement>
  inputRef: RefObject<HTMLInputElement>
  position: ValueSelectorDropdownPosition
  zIndex: CSSProperties['zIndex']
  value: string
  search: string
  onSearchChange: (value: string) => void
  onKeyDown: KeyboardEventHandler
  ariaLabel?: string
  showSearch: boolean
  searchEnabled: boolean
  searchInputClassName: string
  filtered: ValueSelectorOption[]
  optionCount: number
  showEmptyOption: boolean
  emptyOptionAriaLabel: string
  emptyLabel?: string
  placeholder: string
  emptyOptionsMessage: string
  /** Non-selectable status below available options while more choices load. */
  loadingMessage?: string
  highlightIndex: number
  /** Arrow keys moved the highlight; a disabled option then shows its reason. */
  keyboardNavigation: boolean
  onHighlight: (index: number) => void
  onHover: (index: number) => void
  onSelectEmpty: () => void
  onSelectOption: (option: ValueSelectorOption) => void
  onTrailingAction: (option: ValueSelectorOption) => void
  optionClassName: string
  emptyOptionClassName: string
  messageClassName: string
  /** Options are cards: each description is shown whole under its name. */
  optionCards?: boolean
  variant: ValueSelectorVariant
  /** The grouping option whose side list is open, if any. */
  submenuParent?: ValueSelectorOption
  submenuHighlight?: number | null
  submenuRef?: RefObject<HTMLDivElement>
  onSubmenuHover?: (index: number) => void
}

export function ValueSelectorDropdown({
  dropdownRef,
  optionListRef,
  inputRef,
  position,
  zIndex,
  value,
  search,
  onSearchChange,
  onKeyDown,
  ariaLabel,
  showSearch,
  searchEnabled,
  searchInputClassName,
  filtered,
  optionCount,
  showEmptyOption,
  emptyOptionAriaLabel,
  emptyLabel,
  placeholder,
  emptyOptionsMessage,
  loadingMessage,
  highlightIndex,
  keyboardNavigation,
  onHighlight,
  onHover,
  onSelectEmpty,
  onSelectOption,
  onTrailingAction,
  optionClassName,
  emptyOptionClassName,
  messageClassName,
  optionCards = false,
  variant,
  submenuParent,
  submenuHighlight = null,
  submenuRef,
  onSubmenuHover,
}: ValueSelectorDropdownProps) {
  const isFieldVariant = variant === 'field'
  const isCellVariant = variant === 'cell'
  const isCompoundVariant = variant === 'compound'
  const isViewParameterVariant = variant === 'viewParameter'
  const fieldLikeVariant = isFieldVariant || isCellVariant || isCompoundVariant

  // Where the open side list's row and the list itself sit, to place it beside them.
  const [submenuAnchor, setSubmenuAnchor] = useState<{ row: DOMRect; frame: DOMRect } | null>(null)
  const submenuParentId = submenuParent?.id
  useLayoutEffect(() => {
    const row = submenuParentId
      ? Array.from(
          optionListRef.current?.querySelectorAll<HTMLElement>('[data-value-selector-option-id]') ??
            []
        ).find((element) => element.dataset.valueSelectorOptionId === submenuParentId)
      : null
    const frame = dropdownRef.current
    setSubmenuAnchor(
      row && frame
        ? { row: row.getBoundingClientRect(), frame: frame.getBoundingClientRect() }
        : null
    )
  }, [dropdownRef, optionListRef, position, submenuParentId])

  return createPortal(
    <div
      data-portal
      data-placement={position.placedAbove ? 'above' : 'below'}
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: position.width,
        zIndex,
      }}
      onMouseDown={(event) => event.stopPropagation()}
      className={clsx(
        isViewParameterVariant
          ? VIEW_PARAMETER_DROPDOWN_FRAME_CLASS
          : fieldLikeVariant
            ? 'overflow-hidden rounded-md border border-default surface-default shadow-md'
            : 'rounded-md border border-default surface-default p-1 shadow-md',
        isCellVariant && 'table-grid-inline-select-dropdown'
      )}
    >
      {showSearch ? (
        <ValueSelectorSearchInput
          inputRef={inputRef}
          value={search}
          onChange={onSearchChange}
          onKeyDown={onKeyDown}
          ariaLabel={ariaLabel}
          fieldLikeVariant={fieldLikeVariant}
          cellVariant={isCellVariant}
          className={searchInputClassName}
        />
      ) : null}

      <PopoverScrollArea
        ref={optionListRef}
        role="listbox"
        className={clsx(
          !isFieldVariant && MENU_OPTION_LIST_CLASS,
          isCellVariant && 'table-grid-inline-popover-option-list',
          showSearch && !fieldLikeVariant && (isViewParameterVariant ? 'mt-0.5' : 'mt-1')
        )}
        scrollbarGutterClassName={
          isViewParameterVariant
            ? showSearch
              ? VIEW_PARAMETER_DROPDOWN_TRAILING_SCROLLBAR_GUTTER_CLASS
              : VIEW_PARAMETER_DROPDOWN_SCROLLBAR_GUTTER_CLASS
            : fieldLikeVariant
              ? ''
              : undefined
        }
        style={{ maxHeight: position.maxHeight }}
        onKeyDown={!searchEnabled ? onKeyDown : undefined}
      >
        <div
          className={isFieldVariant ? `p-1 ${MENU_OPTION_LIST_CLASS}` : 'contents [&>*]:shrink-0'}
        >
          {optionCount === 0 ? (
            loadingMessage ? null : (
              <div className={messageClassName}>{emptyOptionsMessage}</div>
            )
          ) : filtered.length === 0 && !showEmptyOption ? (
            <div className={messageClassName}>No matching values</div>
          ) : (
            <>
              {showEmptyOption ? (
                <ValueSelectorOptionButton
                  index={0}
                  ariaLabel={emptyOptionAriaLabel}
                  selected={value === ''}
                  onSelect={onSelectEmpty}
                  onHighlight={onHighlight}
                  className={clsx(
                    emptyOptionClassName,
                    isCellVariant
                      ? highlightIndex === 0 && 'table-grid-inline-popover-option--highlighted'
                      : isFieldVariant
                        ? value === ''
                          ? highlightIndex === 0
                            ? 'bg-[color:var(--surface-info-subtle-hover)]'
                            : 'bg-[color:var(--surface-info-subtle)]'
                          : highlightIndex === 0 && 'bg-muted'
                        : highlightIndex === 0 && 'bg-[color:var(--surface-info-subtle)]'
                  )}
                >
                  <span
                    data-value-selector-option-label="true"
                    className={clsx(
                      'flex min-w-0 flex-1 flex-col truncate text-left italic text-muted',
                      value === '' && 'font-medium'
                    )}
                  >
                    <span className="block min-w-0 truncate">
                      {(emptyLabel ?? placeholder) || 'None'}
                    </span>
                    {/* As tall as the described options beside it, for an even list. */}
                    {filtered.some((option) => option.description) ? (
                      <span aria-hidden="true" className="invisible block text-xs leading-4">
                        &nbsp;
                      </span>
                    ) : null}
                  </span>
                </ValueSelectorOptionButton>
              ) : null}
              {filtered.map((option, index) => {
                const optionIndex = showEmptyOption ? index + 1 : index
                const fieldSelected = isFieldVariant && option.id === value
                const button = (
                  <ValueSelectorOptionButton
                    key={option.id}
                    optionId={option.id}
                    expanded={option.children ? submenuParent?.id === option.id : undefined}
                    index={optionIndex}
                    selected={option.id === value}
                    ariaLabel={
                      option.description
                        ? [
                            option.label,
                            option.description,
                            option.secondaryLabel,
                            option.rightLabel,
                          ]
                            .filter(Boolean)
                            .join(' ')
                        : undefined
                    }
                    disabled={option.disabled}
                    disabledReason={option.disabledReason}
                    onSelect={() => onSelectOption(option)}
                    onHighlight={onHighlight}
                    onHover={onHover}
                    className={clsx(
                      optionClassName,
                      option.trailingAction && !option.disabled && '!pr-10',
                      option.disabled
                        ? 'cursor-not-allowed opacity-50'
                        : isCellVariant
                          ? option.id === value
                            ? optionIndex === highlightIndex
                              ? 'table-grid-inline-popover-option--selected-highlighted'
                              : 'table-grid-inline-popover-option--selected'
                            : optionIndex === highlightIndex &&
                              'table-grid-inline-popover-option--highlighted'
                          : isFieldVariant
                            ? fieldSelected
                              ? optionIndex === highlightIndex
                                ? 'bg-[color:var(--surface-info-subtle-hover)]'
                                : 'bg-[color:var(--surface-info-subtle)]'
                              : optionIndex === highlightIndex && 'bg-muted'
                            : optionIndex === highlightIndex &&
                              'bg-[color:var(--surface-info-subtle)]'
                    )}
                  >
                    {option.leading ? (
                      <span className="flex shrink-0 items-center">{option.leading}</span>
                    ) : null}
                    <span
                      data-value-selector-option-label="true"
                      className={clsx(
                        'flex min-w-0 flex-1 items-center gap-2 truncate text-left',
                        option.muted ? 'text-muted' : optionToneTextClass(option.tone),
                        option.italic && 'italic'
                      )}
                    >
                      <span className="flex min-w-0 flex-col">
                        <span
                          className={clsx(
                            'block min-w-0 truncate',
                            (optionCards || option.secondaryLabel || option.id === value) &&
                              'font-medium'
                          )}
                        >
                          {option.label}
                        </span>
                        {option.description ? (
                          <span
                            className={clsx(
                              'block text-xs leading-4 font-normal text-muted',
                              optionCards ? 'mt-0.5 whitespace-normal' : 'truncate'
                            )}
                          >
                            {option.description}
                          </span>
                        ) : null}
                      </span>
                      {option.secondaryLabel ? (
                        <ValueSelectorSecondaryLabel
                          option={option}
                          wraps={Boolean(option.description)}
                          className="text-muted"
                        />
                      ) : null}
                      {option.hint ? (
                        // The option chooses on press; the icon's press is its own.
                        <span
                          className={clsx(
                            'inline-flex shrink-0',
                            !option.secondaryLabel && 'ml-auto'
                          )}
                          onPointerDown={(event) => event.stopPropagation()}
                        >
                          <InfoHint
                            content={option.hint}
                            label={`About ${option.label}`}
                            size="sm"
                          />
                        </span>
                      ) : null}
                    </span>
                    {option.rightLabel ? (
                      <span className="ml-2 shrink-0 text-[10px] font-normal italic text-muted">
                        {option.rightLabel}
                      </span>
                    ) : null}
                    {option.children ? (
                      <ChevronDownIcon
                        size={11}
                        className="ml-2 shrink-0 -rotate-90 text-faint"
                        aria-hidden="true"
                      />
                    ) : null}
                  </ValueSelectorOptionButton>
                )
                if (option.trailingAction && !option.disabled) {
                  const action = option.trailingAction
                  return (
                    // A sibling of the option, not inside it: a button can't hold one.
                    <div key={option.id} className="relative">
                      {button}
                      <IconButton
                        label={`${action.label}: ${option.label}`}
                        tooltip={action.label}
                        wrapperClassName="absolute right-1 top-1/2 -translate-y-1/2"
                        className="flex h-8 w-8 items-center justify-center rounded text-muted transition-colors hover:bg-[color:var(--bg-emphasis)] hover:text-default"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => {
                          event.stopPropagation()
                          onTrailingAction(option)
                        }}
                      >
                        {action.icon}
                      </IconButton>
                    </div>
                  )
                }
                return option.disabled && option.disabledReason ? (
                  <Tooltip
                    key={option.id}
                    content={option.disabledReason}
                    fullWidth
                    forceOpen={keyboardNavigation && optionIndex === highlightIndex}
                  >
                    {button}
                  </Tooltip>
                ) : (
                  button
                )
              })}
            </>
          )}
          {loadingMessage ? (
            <div role="status" className={messageClassName}>
              <span className="text-loading-shimmer">{loadingMessage}</span>
            </div>
          ) : null}
        </div>
      </PopoverScrollArea>
      {submenuParent && submenuAnchor && submenuRef && onSubmenuHover ? (
        <ValueSelectorSubmenu
          parent={submenuParent}
          rowRect={submenuAnchor.row}
          frameRect={submenuAnchor.frame}
          value={value}
          highlightIndex={submenuHighlight}
          submenuRef={submenuRef}
          zIndex={zIndex}
          optionClassName={optionClassName}
          onSelect={onSelectOption}
          onHover={onSubmenuHover}
        />
      ) : null}
    </div>,
    document.body
  )
}
